import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardTabs } from "../../../../../shared/components/tabs/StandardTabs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { MERCHANT_REPORTS_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import MeetingServiceTab from "../meetingService/pages/MeetingServiceTab"
import OrdersForCalendarTab from "../ordersForCalendar/pages/OrdersForCalendarTab"
import {
    DashboardNotifications,
    MeetingServiceOrderStatus,
    MeetingServiceOrdersView,
} from "../meetingService/meetingServiceTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { fetchDashboardNotifications, MerchantHomeQuery, NotificationsStatus } from "../../home/merchantHomeApi"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { OrderStatusType } from "../../home/merchantHomeTypes"
import { IconButton } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarDays, faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { Stack } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import Shortcuts from "../components/Shortcuts"
import { useState, useCallback, useEffect } from "react"
import { MeetingServiceFiltersProvider } from "../meetingService/context/MeetingServiceFiltersContext"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { fetchMeetingServiceOrders, MerchantMeetingServiceOrdersQuery } from "../meetingService/meetingServiceApi"
import { useExportMeetingServiceOrdersToCsv } from "../meetingService/components/useExportMeetingServiceOrdersToCsv"
import { useDateTime } from "../../../../localization/useDateTime"

type StyleProps = Readonly<{
    isCalendarActive: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isCalendarActive }) => ({
    calendarButton: {
        padding: "0.75rem",
        border: `1px solid ${!isCalendarActive ? theme.palette.divider : "white"}`,
        borderRadius: "0.75rem",
        color: !isCalendarActive ? "black" : "white",
        backgroundColor: isCalendarActive ? "black" : "white",
        width: "2.5rem",
        height: "2.5rem",
        "&:hover": {
            backgroundColor: isCalendarActive ? "#333" : "inherit",
        },
    },
    exportButton: {
        border: `1px solid ${theme.palette.divider}`,
    },
    rightActions: {
        flexDirection: "row",
        position: "absolute",
        top: theme.spacing(0.5),
        right: theme.spacing(2),
        gap: theme.spacing(1.25),
    },
}))

export function getOrdersByType(type: DashboardNotifications, notifications?: NotificationsStatus[]): number {
    return notifications?.find((n) => n.type === type)?.numberOfOrders ?? 0
}

const selectMeetingService = (data: any) => data?.orders || []

export function MerchantProductionMeetingService() {
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const [isCalendarActive, setIsCalendarActive] = useState(false)
    const { classes } = useStyles({ isCalendarActive })
    const dateTimeFactory = useDateTime()
    const todayDate = dateTimeFactory.now()

    const dashboardNotifications = useApiQuery(fetchDashboardNotifications, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS,
        dependencies: {
            merchantId,
            params: { status: OrderStatusType.READY_FOR_INVOICE.toUpperCase() },
        },
    })

    const [shouldExport, setShouldExport] = useState(false)

    const allOrdersForExport = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: {
            merchantId,
            view: MeetingServiceOrdersView.PRODUCTION,
        },
        select: selectMeetingService,
        enabled: shouldExport,
    })

    const exportAllOrdersFn = useExportMeetingServiceOrdersToCsv({
        data: allOrdersForExport.data ?? [],
        startDate: todayDate.startOf("year"),
        endDate: todayDate.endOf("year"),
        includeVatInLabels: true,
    })

    const handleExportAll = useCallback(() => {
        setShouldExport(true)
    }, [])

    useEffect(() => {
        if (shouldExport && allOrdersForExport.data && allOrdersForExport.data.length > 0) {
            exportAllOrdersFn()
            setShouldExport(false)
        }
    }, [shouldExport, allOrdersForExport.data, exportAllOrdersFn])

    const toBeBookedAmount = getOrdersByType(
        DashboardNotifications.TO_BE_BOOKED,
        dashboardNotifications.data?.notifications,
    )
    const awaitingForApprovalAmount = getOrdersByType(
        DashboardNotifications.NOT_APPROVED,
        dashboardNotifications.data?.notifications,
    )

    const productionAmount = getOrdersByType(
        DashboardNotifications.PRODUCTION_MEETING_SERVICE_ORDERS,
        dashboardNotifications.data?.notifications,
    )

    const removeTab = () => setIsCalendarActive(true)

    const onTabChange = () => {
        if (isCalendarActive) {
            setIsCalendarActive(false)
        }
    }

    const tabs = [
        {
            label: l10n.getString("production-tab-orders", {
                amount: `(${productionAmount})`,
            }),
            component: (
                <MeetingServiceTab view={MeetingServiceOrdersView.PRODUCTION} />
            ),
        },
        {
            label: l10n.getString("planning-tab-not-approved", {
                amount: `(${awaitingForApprovalAmount})`,
            }),
            component: (
                <MeetingServiceTab
                    tab={MeetingServiceOrderStatus.NOT_APPROVED}
                    view={MeetingServiceOrdersView.NOT_APPROVED}
                />
            ),
        },
        {
            label: l10n.getString("planning-tab-to-be-booked", {
                amount: `(${toBeBookedAmount})`,
            }),
            component: (
                <MeetingServiceTab
                    tab={MeetingServiceOrderStatus.TO_BE_BOOKED}
                    view={MeetingServiceOrdersView.TO_BE_BOOKED}
                />
            ),
        },
    ]

    return (
        <>
            <LocalizedStrict id="production-meeting-service-page-header" attrs={{ title: true }}>
                <PageHeader page={Page.PRODUCTION_MEETING_SERVICE} title="Meeting service">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-production">
                            <Breadcrumb>Production</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="production-meeting-service-breadcrumb">
                            <Breadcrumb>Meeting service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <MeetingServiceFiltersProvider>
                <Stack position="relative">
                    <StandardTabs tabs={tabs} ignoreRender={isCalendarActive} onTabChange={onTabChange} />
                    {isCalendarActive && <OrdersForCalendarTab />}
                    <Stack className={classes.rightActions}>
                        <IconButton className={classes.calendarButton} onClick={removeTab}>
                            <FontAwesomeIcon icon={faCalendarDays} size="xs" />
                        </IconButton>
                        <LocalizedStrict id="meeting-service-table-toolbar-export-all">
                            <StandardButton
                                fullWidth={false}
                                className={classes.exportButton}
                                size="small"
                                variant="outlined"
                                onClick={handleExportAll}
                                startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
                            >
                                Export all
                            </StandardButton>
                        </LocalizedStrict>
                        <Shortcuts />
                    </Stack>
                </Stack>
            </MeetingServiceFiltersProvider>
        </>
    )
}
